import React, { useMemo, useState, useEffect } from 'react';
import { useColor, useTheme } from '../../../context/ColorContext';
import { useFanDeck } from '../../../hooks/useFanDeck';

// Memoized individual card for maximum performance
const FanCard = React.memo(({
    color,
    index,
    activeColorIndex,
    isMobile,
    isDark,
    onClick,
    scaleFactor = 1,
    extra = null
}) => {
    const diff = index - activeColorIndex;
    const isActive = index === activeColorIndex;

    // Performance optimization: limit rendering expensive styles for distant cards
    const isVisible = Math.abs(diff) <= 8;
    if (!isVisible && !isActive) return null;

    // Responsive calculations
    const rotation = diff * (isMobile ? 10 : 8);
    const xOffsetMultiplier = isMobile ? (window.innerWidth < 380 ? 30 : 40) : 75;
    const xOffset = diff * xOffsetMultiplier * scaleFactor;
    const yOffset = Math.abs(diff) * (isMobile ? 12 : 15) * scaleFactor;
    const scale = (isActive ? 1.1 : 1 - Math.abs(diff) * 0.05) * scaleFactor;
    const zIndex = 50 - Math.abs(diff);
    const opacity = Math.abs(diff) > 4 ? (Math.max(0, 1 - (Math.abs(diff) - 4) * 0.25)) : 1;

    const nameParts = color.name.split(' ');
    const firstName = nameParts[0];
    const secondName = nameParts.slice(1).join(' ');

    return (
        <div
            className={`absolute bottom-0 rounded-t-[1.5rem] lg:rounded-t-[2.5rem] shadow-2xl origin-bottom cursor-grab active:cursor-grabbing preserve-3d transition-brightness duration-300
                ${isDark ? 'border-[3px] lg:border-4 border-white/40' : 'border-none'}
                ${isActive ? 'brightness-110' : 'brightness-90 hover:brightness-100'}
            `}
            style={{
                width: `${(isMobile ? 130 : 220) * scaleFactor}px`,
                height: `${(isMobile ? 320 : 500) * scaleFactor}px`,
                backgroundColor: color.code,
                transform: `translate3d(${xOffset}px, ${yOffset}px, 0) rotate(${rotation}deg) scale(${scale})`,
                zIndex: zIndex,
                opacity: opacity,
                willChange: 'transform',
                transition: 'transform 0.45s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.5s ease',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden'
            }}
            onClick={() => onClick(index)}
        >
            {/* Card Content - Only render text for cards close to center for perf */}
            {Math.abs(diff) < 2 && (
                <div className="absolute top-6 lg:top-8 left-0 right-0 text-center px-4 animate-in fade-in duration-500">
                    <div className={`${isDark ? 'bg-white/20' : 'bg-black/10'} backdrop-blur-md rounded-full px-2 py-0.5 lg:px-3 lg:py-1 inline-block mb-2 lg:mb-4`}>
                        <span className={`${isDark ? 'text-white' : 'text-slate-900'} text-[8px] lg:text-xs font-bold tracking-widest uppercase`}>
                            {color.code}
                        </span>
                    </div>
                    <h3 className="text-white text-base lg:text-3xl font-black uppercase tracking-tighter leading-none drop-shadow-md transform -rotate-90 md:rotate-0 mt-8 md:mt-0">
                        {firstName}<br />
                        {secondName && <span className="text-[10px] lg:text-xl font-light opacity-80">{secondName}</span>}
                    </h3>
                </div>
            )}

            {/* Texture Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none rounded-t-[1.5rem] lg:rounded-t-[2.5rem]" />
            <div className="absolute bottom-12 lg:bottom-16 left-0 right-0 flex justify-center pointer-events-none">
                <div className={`w-3 h-3 lg:w-4 lg:h-4 rounded-full border ${isDark ? 'bg-white/50 border-white' : 'bg-black/50 border-white/50'}`} />
            </div>

            {/* Injected Extra Content (e.g. Visualizer Button) - Rendered LAST for highest stacking */}
            {isActive && extra && (
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-full flex justify-center animate-in zoom-in-50 duration-500 pointer-events-auto z-[60]">
                    {extra}
                </div>
            )}
        </div>
    );
});

FanCard.displayName = 'FanCard';

const FanDeck = ({ isMobile, enableWheel = false, lift = false, activeCardExtra = null, overrideColors = null }) => {
    const { activeColorIndex, setActiveColorIndex, brandData, activeColor } = useColor();
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    // Determine which colors to use (full brand or specific collection)
    const COLORS = overrideColors || brandData.COLORS;

    // Map the global activeColor to an index within the current (potentially filtered) COLORS list
    const localActiveIndex = useMemo(() => {
        if (!overrideColors) return activeColorIndex;
        const index = COLORS.findIndex(c => (c.id && c.id === activeColor.id) || c.code === activeColor.code);
        return index !== -1 ? index : 0;
    }, [overrideColors, COLORS, activeColor, activeColorIndex]);

    // Function to update color while mapping local index back to global COLORS
    const handleColorSelection = (localIndex) => {
        if (!overrideColors) {
            setActiveColorIndex(localIndex);
            return;
        }

        const selectedColor = COLORS[localIndex];
        const globalIndex = brandData.COLORS.findIndex(c => (c.id && c.id === selectedColor.id) || c.code === selectedColor.code);
        if (globalIndex !== -1) {
            setActiveColorIndex(globalIndex);
        }
    };

    // Handle responsive scaling
    const [scaleFactor, setScaleFactor] = useState(1);

    useEffect(() => {
        const calculateScale = () => {
            if (window.innerWidth < 1024) {
                // For mobile, scale based on width but also check height for very short devices
                const widthScale = Math.min(1, (window.innerWidth - 40) / 350);
                const heightScale = Math.min(1, (window.innerHeight - 200) / 400);
                setScaleFactor(Math.min(widthScale, heightScale, 1.1));
            } else {
                setScaleFactor(1);
            }
        };

        calculateScale();
        window.addEventListener('resize', calculateScale);
        return () => window.removeEventListener('resize', calculateScale);
    }, []);

    const { containerRef, handlers } = useFanDeck(
        localActiveIndex,
        handleColorSelection,
        COLORS,
        { enableWheel }
    );

    // Virtualization: Only render a slice of the colors array around the active index
    const visibleCards = useMemo(() => {
        const windowSize = isMobile ? 6 : 8;
        const start = Math.max(0, localActiveIndex - windowSize);
        const end = Math.min(COLORS.length, localActiveIndex + windowSize + 1);

        return COLORS.slice(start, end).map((color, i) => ({
            color,
            originalIndex: start + i
        }));
    }, [localActiveIndex, COLORS, isMobile]);

    return (
        <div
            className="relative w-full h-full flex items-end justify-center perspective-[1500px] overflow-visible z-10"
            ref={containerRef}
            {...handlers}
        >
            <div className={`relative w-full h-full flex items-end justify-center transition-all duration-500 ${lift ? 'mb-[-40px] lg:mb-[-100px]' : 'mb-[-60px] lg:mb-[-320px]'}`}>
                {visibleCards.map(({ color, originalIndex }) => (
                    <FanCard
                        key={color.id || originalIndex}
                        color={color}
                        index={originalIndex}
                        activeColorIndex={localActiveIndex}
                        isMobile={isMobile}
                        isDark={isDark}
                        onClick={handleColorSelection}
                        scaleFactor={scaleFactor}
                        extra={originalIndex === localActiveIndex ? activeCardExtra : null}
                    />
                ))}
            </div>

            <div className={`absolute bottom-24 lg:bottom-20 pointer-events-none animate-pulse text-[9px] lg:text-xs font-black uppercase tracking-[0.3em] ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                ← Sürükle & Keşfet →
            </div>
        </div>
    );
};

export default FanDeck;
